import { useEffect } from 'preact/hooks';
import { NPCS } from '../../content/npcs';
import { closePanel, ui } from '../../ui/app/store';
import { EMOTES, EMOTES_PER_DAY, EMOTE_BOND_RANGE, profileFor } from './data';
import { nearestBondNpc, performPlayerEmote } from './emotes';
import { dailyUsed } from './logic';
import { bondsGame } from './runtime';

/** Stick-figure pictograms for the eight emotes (24×24, stroke-drawn). */
export function EmoteGlyph({ id }: { id: string }) {
  const s = { fill: 'none', stroke: 'currentColor', 'stroke-width': 1.7, 'stroke-linecap': 'round' as const, 'stroke-linejoin': 'round' as const };
  const head = (x: number, y: number) => <circle cx={x} cy={y} r="2.3" {...s} />;
  switch (id) {
    case 'wave':
      return (
        <svg viewBox="0 0 24 24" class="em-glyph">
          {head(11, 5)}
          <path d="M11 7.5 V15 M11 15 L8 21 M11 15 L14 21 M11 9.5 L7.5 13.5 M11 9.5 L15 7 L16 3" {...s} />
          <path d="M18.5 2.5 q1.6 1.4 1 3.6 M20.5 1.5 q2.2 2.2 1.2 5.2" {...s} stroke-width={1.2} />
        </svg>
      );
    case 'cheer':
      return (
        <svg viewBox="0 0 24 24" class="em-glyph">
          {head(12, 6.5)}
          <path d="M12 9 V15.5 M12 15.5 L8.5 21 M12 15.5 L15.5 21 M12 10.5 L7 4 M12 10.5 L17 4" {...s} />
          <path d="M4 2 l0.8 1.8 M20 2 l-0.8 1.8 M12 1 v1.6" {...s} stroke-width={1.2} />
        </svg>
      );
    case 'bow':
      return (
        <svg viewBox="0 0 24 24" class="em-glyph">
          {head(7, 8.5)}
          <path d="M9 9.8 L14 13.5 L14 21 M14 13.5 L16.5 21 M10.5 11 L9 16" {...s} />
        </svg>
      );
    case 'dance':
      return (
        <svg viewBox="0 0 24 24" class="em-glyph">
          {head(10.5, 5)}
          <path d="M10.5 7.5 L12 14.5 M12 14.5 L9 21 M12 14.5 L17 17.5 M11 9.5 L6 7 M11 9.5 L15.5 6" {...s} />
          <path d="M18.5 3 v5 M18.5 3 l3 -1 v4.6" {...s} stroke-width={1.3} />
          <circle cx="17.6" cy="8.2" r="1" fill="currentColor" />
          <circle cx="20.6" cy="6.8" r="1" fill="currentColor" />
        </svg>
      );
    case 'sit':
      return (
        <svg viewBox="0 0 24 24" class="em-glyph">
          {head(8.5, 6)}
          <path d="M8.5 8.5 V15.5 L15 15.5 L16 21 M8.5 11 L13 13" {...s} />
          <path d="M4 21 H21" {...s} stroke-width={1.2} />
        </svg>
      );
    case 'laugh':
      return (
        <svg viewBox="0 0 24 24" class="em-glyph">
          {head(12, 6)}
          <path d="M12 8.5 V15 M12 15 L9 21 M12 15 L15 21 M12 10.5 L9 13 L11 14.5 M12 10.5 L15 13 L13 14.5" {...s} />
          <path d="M16.5 3.5 l2 -1 M17 6 h2.4 M7.5 3.5 l-2 -1 M7 6 h-2.4" {...s} stroke-width={1.2} />
        </svg>
      );
    case 'point':
      return (
        <svg viewBox="0 0 24 24" class="em-glyph">
          {head(8, 5)}
          <path d="M8 7.5 V15 M8 15 L5.5 21 M8 15 L10.5 21 M8 9.5 L19 9.5 M8 10 L6 13.5" {...s} />
          <path d="M16.5 7.5 L19.5 9.5 L16.5 11.5" {...s} stroke-width={1.3} />
        </svg>
      );
    case 'salute':
      return (
        <svg viewBox="0 0 24 24" class="em-glyph">
          {head(11, 6)}
          <path d="M11 8.5 V15.5 M11 15.5 L9 21 M11 15.5 L13 21 M11 10.5 L8 14 M11 10.5 L16 9 L13.5 4.6" {...s} />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" class="em-glyph">
          {head(12, 6)}
        </svg>
      );
  }
}

/** Emote wheel (Y): eight emotes around a hub; click or press 1–8. */
export function EmotesPanel() {
  void ui.version.value;
  const game = bondsGame.current;
  const choose = (id: string) => {
    if (game) performPlayerEmote(game, id);
    closePanel();
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const m = /^Digit([1-8])$/.exec(e.code);
      if (!m || e.repeat) return;
      const def = EMOTES[Number(m[1]) - 1];
      if (!def) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      choose(def.id);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });
  const p = game?.player;
  const prog = game?.progression;
  const npc = game && p ? nearestBondNpc(game, p, EMOTE_BOND_RANGE) : null;
  const left = prog && game ? EMOTES_PER_DAY - dailyUsed(prog.char, game.account.day).emotes : 0;
  const prof = npc?.npcId ? profileFor(npc.npcId) : null;
  const R = 132;
  return (
    <div class="em-scrim interactive" onPointerDown={(e) => e.target === e.currentTarget && closePanel()}>
      <div class="em-wheel" onPointerDown={(e) => e.stopPropagation()}>
        <svg class="em-ring" viewBox="-180 -180 360 360">
          <circle r="170" class="em-ring-outer" />
          <circle r="96" class="em-ring-inner" />
          {EMOTES.map((_, i) => {
            const a = ((i + 0.5) / EMOTES.length) * Math.PI * 2 - Math.PI / 2;
            return <line key={i} x1={Math.cos(a) * 98} y1={Math.sin(a) * 98} x2={Math.cos(a) * 168} y2={Math.sin(a) * 168} class="em-spoke" />;
          })}
        </svg>
        {EMOTES.map((em, i) => {
          const a = (i / EMOTES.length) * Math.PI * 2 - Math.PI / 2;
          const liked = prof?.likedEmote === em.id;
          const disliked = prof?.dislikedEmote === em.id;
          return (
            <button
              key={em.id}
              class={`em-btn${liked ? ' liked' : ''}${disliked ? ' disliked' : ''}`}
              style={{ left: `calc(50% + ${Math.cos(a) * R}px)`, top: `calc(50% + ${Math.sin(a) * R}px)` }}
              onClick={() => choose(em.id)}
              title={`${em.name}${em.loop ? ' (until you move)' : ''}`}
            >
              <EmoteGlyph id={em.id} />
              <span class="em-name">{em.name}</span>
              <span class="em-key">{i + 1}</span>
            </button>
          );
        })}
        <div class="em-hub">
          <div class="em-hub-title">Emotes</div>
          {npc?.npcId ? (
            <div class="em-hub-bond">
              <span>♥ {NPCS[npc.npcId]?.name ?? npc.name}</span>
              <small>{left > 0 ? `Counts toward your bond · ${left} left today` : 'No bond emotes left today'}</small>
            </div>
          ) : (
            <div class="em-hub-hint">
              Press <span class="keycap">1</span>–<span class="keycap">8</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
